import React, { useState, useEffect, useMemo } from "react";
import Select, { GroupBase, OptionsOrGroups } from "react-select";
import {
  categories,
  MainCategory,
  CategoryOption,
  getTranslatedCategories,
  getTranslationKeyFromValue,
  getTranslatedValueFromKey,
} from "../data/categories";
import { useTranslationReload } from "../hooks/useTranslationReload";
import { useQuery } from "@tanstack/react-query";
import { getAllCategories } from "../backend/api/categories";

// Definisci un tipo generico per le opzioni di select
type SelectOption = { value: string; label: string };

interface CategorySelectProps {
  value: CategoryOption | null;
  onChange: (value: CategoryOption | null) => void;
  // Indica se i valori nel backend sono già chiavi di traduzione
  useTranslationKeys?: boolean;
  // Indica se usare le categorie dal backend invece di quelle hardcoded
  useBackendCategories?: boolean;
  className?: string;
}

const CategorySelect: React.FC<CategorySelectProps> = ({
  value,
  onChange,
  useTranslationKeys = false,
  useBackendCategories = false,
  className = "",
}) => {
  const { t, i18n } = useTranslationReload();
  const [selectedMain, setSelectedMain] = useState<MainCategory | null>(
    value?.main || null
  );
  const [selectedSub, setSelectedSub] = useState<string | null>(
    value?.sub || null
  );
  const [selectedType, setSelectedType] = useState<string | null>(
    value?.type || null
  );
  // Forza l'aggiornamento dei dati delle categorie quando cambia la lingua
  const [localCategories, setLocalCategories] =
    useState<Record<string, Record<string, string[]>>>(categories);

  // Query per le categorie dal backend
  const { data: backendCategories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ["categories", i18n.language],
    queryFn: () => getAllCategories(i18n.language),
    enabled: useBackendCategories, // Solo se useBackendCategories è true
    staleTime: 5 * 60 * 1000, // 5 minuti
  });

  // Mappa le categorie dal backend in formato gerarchico
  const backendCategoriesHierarchical = useMemo(() => {
    if (!backendCategories || !Array.isArray(backendCategories)) return {};

    const result: Record<
      string,
      Record<string, Array<{ id: number; type: string }>>
    > = {};

    backendCategories.forEach((item) => {
      if (!result[item.category]) {
        result[item.category] = {};
      }
      if (!result[item.category][item.subcategory]) {
        result[item.category][item.subcategory] = [];
      }
      result[item.category][item.subcategory].push({
        id: item.id,
        type: item.type,
      });
    });

    return result;
  }, [backendCategories]);

  // Log per il debug
  useEffect(() => {
    console.log("CategorySelect - Props value:", value);
    console.log("CategorySelect - useTranslationKeys:", useTranslationKeys);
    console.log("CategorySelect - useBackendCategories:", useBackendCategories);
    console.log("CategorySelect - Backend categories:", backendCategories);
  }, [value, useTranslationKeys, useBackendCategories, backendCategories]);

  // Converte i valori dal backend in valori visualizzabili
  useEffect(() => {
    if (value && useTranslationKeys) {
      // Se i valori sono chiavi di traduzione, convertiamoli in valori tradotti
      const translatedValue = {
        main: getTranslatedValueFromKey(value.main as string) as MainCategory,
        sub: getTranslatedValueFromKey(value.sub as string),
        type: getTranslatedValueFromKey(value.type as string),
      };
      console.log("Translated values:", translatedValue);
      setSelectedMain(translatedValue.main);
      setSelectedSub(translatedValue.sub);
      setSelectedType(translatedValue.type);
    } else if (value) {
      // Altrimenti, usa i valori così come sono
      setSelectedMain(value.main);
      setSelectedSub(value.sub);
      setSelectedType(value.type);
    }
  }, [value, useTranslationKeys]);

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "rgb(51, 24, 85)",
      borderColor: state.isFocused ? "rgba(90, 24, 196, 0.6)" : "#6e11b04d",
      borderRadius: "0.5rem",
      fontSize: "14px",
      minHeight: "40px",
      paddingLeft: "4px",
      color: "white",
      boxShadow: "none",
      "&:hover": {
        borderColor: "rgba(90, 24, 196, 0.6)",
      },
    }),

    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1F1235",
      borderRadius: "0.75rem",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      zIndex: 100,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#5A18C4"
        : state.isFocused
        ? "rgba(90, 24, 196, 0.2)"
        : "transparent",
      color: "white",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#5A18C4",
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#6a7282 !important",
      fontSize: "14px !important",
    }),
    input: (base: any) => ({
      ...base,
      color: "white",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.4)",
      "&:hover": {
        color: "white",
      },
    }),
  };

  useEffect(() => {
    // Se usiamo le categorie dal backend, non aggiorniamo localCategories
    if (useBackendCategories) return;

    try {
      const translated = getTranslatedCategories();
      // Assicuriamoci che il risultato sia un oggetto valido prima di aggiornare lo stato
      if (
        translated &&
        typeof translated === "object" &&
        Object.keys(translated).length > 0
      ) {
        setLocalCategories(
          translated as Record<string, Record<string, string[]>>
        );
      } else {
        console.warn(
          "getTranslatedCategories returned an invalid object, using default categories"
        );
        setLocalCategories(categories); // Usa le categorie predefinite come fallback
      }
    } catch (error) {
      console.error("Error in getTranslatedCategories:", error);
      setLocalCategories(categories); // Usa le categorie predefinite in caso di errore
    }
  }, [t, useBackendCategories]);

  const mainOptions = React.useMemo(() => {
    if (useBackendCategories && backendCategoriesHierarchical) {
      return Object.keys(backendCategoriesHierarchical).map((cat) => ({
        value: cat,
        label: cat,
      })) as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
    }

    if (!localCategories) return [];
    return Object.keys(localCategories).map((cat) => ({
      value: cat,
      label: cat,
    })) as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
  }, [localCategories, useBackendCategories, backendCategoriesHierarchical]);

  const getSubOptions = (main: MainCategory) => {
    try {
      if (useBackendCategories && backendCategoriesHierarchical) {
        if (!main || !backendCategoriesHierarchical[main]) {
          console.warn(
            `Category "${main}" not found in backendCategoriesHierarchical`
          );
          return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
        }

        return Object.keys(backendCategoriesHierarchical[main]).map((sub) => ({
          value: sub,
          label: sub,
        })) as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
      }

      if (!main || !localCategories) {
        console.warn("Missing main category or localCategories");
        return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
      }

      const mainCategory = localCategories[main];
      if (!mainCategory || typeof mainCategory !== "object") {
        console.warn(`Category "${main}" not found in localCategories`);
        return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
      }

      return Object.keys(mainCategory).map((sub) => ({
        value: sub,
        label: sub,
      })) as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
    } catch (error) {
      console.error("Error in getSubOptions:", error);
      return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
    }
  };

  const getTypeOptions = (main: MainCategory, sub: string) => {
    try {
      if (useBackendCategories && backendCategoriesHierarchical) {
        if (!main || !sub || !backendCategoriesHierarchical[main]) {
          console.warn("Missing parameters in getTypeOptions (backend)", {
            main,
            sub,
            hasBackendCategories: !!backendCategoriesHierarchical,
          });
          return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
        }

        const mainCategory = backendCategoriesHierarchical[main];
        const subCategory = mainCategory[sub];

        if (!Array.isArray(subCategory)) {
          console.warn(
            `Sub-category "${sub}" is not an array or not found in backend`
          );
          return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
        }

        return subCategory.map((item) => ({
          value: item.id.toString(), // Usa l'ID come valore
          label: item.type, // Usa il tipo come label
        })) as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
      }

      if (!main || !sub || !localCategories || !localCategories[main]) {
        console.warn("Missing parameters in getTypeOptions", {
          main,
          sub,
          hasLocalCategories: !!localCategories,
        });
        return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
      }

      const mainCategory = localCategories[main];
      const subCategory = mainCategory[sub as keyof typeof mainCategory];

      if (!Array.isArray(subCategory)) {
        console.warn(`Sub-category "${sub}" is not an array or not found`);
        return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
      }

      return subCategory.map((type) => ({
        value: type,
        label: type,
      })) as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
    } catch (error) {
      console.error("Error in getTypeOptions:", error);
      return [] as OptionsOrGroups<SelectOption, GroupBase<SelectOption>>;
    }
  };

  const handleMainChange = (option: any) => {
    const main = option?.value as MainCategory;
    console.log("Main category selected:", main);
    setSelectedMain(main);
    setSelectedSub(null);
    setSelectedType(null);
    onChange(null);
  };

  const handleSubChange = (option: any) => {
    const sub = option?.value || null;
    console.log("Subcategory selected:", sub);
    setSelectedSub(sub);
    setSelectedType(null);
    onChange(null);
  };

  const handleTypeChange = (option: any) => {
    if (!selectedMain || !selectedSub) return;

    console.log("Type selected:", option?.value);
    setSelectedType(option?.value);

    if (option) {
      let categoryOption: CategoryOption;

      if (useBackendCategories) {
        // Se usiamo le categorie del backend, l'ID è il valore
        categoryOption = {
          main: selectedMain,
          sub: selectedSub,
          type: option.value, // Questo è l'ID della categoria
        };
      } else if (useTranslationKeys) {
        // Se usiamo chiavi di traduzione, converti i valori tradotti in chiavi
        categoryOption = {
          main: getTranslationKeyFromValue(selectedMain as string),
          sub: getTranslationKeyFromValue(selectedSub as string),
          type: getTranslationKeyFromValue(option.value as string),
        } as CategoryOption;
      } else {
        // Altrimenti, usa i valori tradotti direttamente
        categoryOption = {
          main: selectedMain,
          sub: selectedSub,
          type: option.value,
        };
      }

      console.log("Final category option:", categoryOption);
      onChange(categoryOption);
    } else {
      onChange(null);
    }
  };

  // Loading state per le categorie del backend
  if (useBackendCategories && isLoadingCategories) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500">
          {t("loading", "Caricamento categorie...")}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 isMultiSelect ${className}`}>
      <label
        className="block text-white text-[14px] font-[400] mb-1"
        htmlFor="main"
      >
        {t("forms.mainCategory", "Main Category")}
      </label>
      <Select
        styles={selectStyles}
        className="rounded-xl transition-colors"
        placeholder={t("forms.selectCategory", "Seleziona Categoria")}
        value={
          selectedMain ? { value: selectedMain, label: selectedMain } : null
        }
        options={mainOptions}
        onChange={handleMainChange}
        isSearchable={false}
        isLoading={useBackendCategories && isLoadingCategories}
      />

      {selectedMain && (
        <>
          <label
            className="block text-white text-[14px] font-[400] mb-1"
            htmlFor="sub"
          >
            {t("forms.subCategory", "Sub Category")}
          </label>
          <Select
            styles={selectStyles}
            className="rounded-xl transition-colors"
            placeholder={t(
              "forms.selectSubcategory",
              "Seleziona Sottocategoria"
            )}
            value={
              selectedSub ? { value: selectedSub, label: selectedSub } : null
            }
            options={getSubOptions(selectedMain)}
            onChange={handleSubChange}
            isSearchable={false}
          />
        </>
      )}

      {selectedMain && selectedSub && (
        <>
          <label
            className="block text-white text-[14px] font-[400] mb-1"
            htmlFor="type"
          >
            {t("forms.type", "Type")}
          </label>
          <Select
            styles={selectStyles}
            className="rounded-xl transition-colors"
            placeholder={t("forms.selectType", "Seleziona Tipo")}
            value={(() => {
              if (!selectedType) return null;
              // Trova il label corrispondente all'ID selezionato
              const options = getTypeOptions(selectedMain, selectedSub).filter(
                (opt) => "value" in opt
              ) as SelectOption[];
              const found = options.find((opt) => opt.value === selectedType);
              return found
                ? found
                : { value: selectedType, label: selectedType };
            })()}
            options={getTypeOptions(selectedMain, selectedSub)}
            onChange={handleTypeChange}
            isSearchable={false}
            menuPlacement="auto"
          />
        </>
      )}
    </div>
  );
};

export default CategorySelect;
